<template>
  <step-container
    :required-data="requiredData"
    :data="data"
    :on-submit="submit"
    :has-steps="isOnboarding"
    :has-changes="hasChanges"
    :hide-secondary-actions="hideSecondaryActions"
    v-bind="$attrs"
    :name="'update-company-publication'"
  >
    <publication
      v-if="isOnboarding"
      :data="localData"
      is-onboarding
      :save-ambassador-image="saveAmbassadorImage"
      :save-company-image="saveImage"
      :variant="variant"
      :showIntegrationWarning="showIntegrationWarning"
      @update:data="updateData"
    />

    <v-card
      v-else
      flat
    >
      <publication
        class="mx-4"
        :data="localData"
        :save-ambassador-image="saveAmbassadorImage"
        :save-company-image="saveImage"
        :variant="variant"
        :delete-company-image="deleteImage"
        @update:data="updateData"
      />
    </v-card>
  </step-container>
</template>

<script>
import Publication from '@/components/forms/Publication'
import bus, { eventNames } from '@/lib/eventBus'
import brandingMixin from '@/mixins/branding.js'
import featureMixin from '@/mixins/feature'
import PENDING_CHANGES_FOR_REGENERATION from '@/queries/PendingChangesForRegeneration.gql'

import COMPANY from './queries/Company.gql'
import DELETE_COMPANY_IMAGE from './queries/DeleteCompanyImage.gql'
import UPDATE_COMPANY_AMBASSADOR from './queries/UpdateAmbassador.gql'
import UPDATE_COMPANY_AUTOMATIC_INTEGRATION from './queries/UpdateCompanyAutomaticIntegration.gql'
import UPDATE_COMPANY_THEME from './queries/UpdateCompanyTheme.gql'
import UPDATE_IS_REFERENCE_CUSTOMER from './queries/UpdateIsReferenceCustomer.gql'
import UPDATE_ONBOARDING_COMPANY_IMAGES from './queries/UpdateOnboardingCompanyImages.gql'
import UPDATE_SLOGAN from './queries/UpdateSlogan.gql'
import StepContainer from './StepContainer.vue'

export default {
  components: { StepContainer, Publication },
  mixins: [featureMixin, brandingMixin],
  props: {
    companyId: {
      type: String,
      required: true,
    },
    slogan: {
      type: String,
      default: '',
    },
    ambassador: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
    images: {
      type: Object,
      required: true,
    },
    isReferenceCustomer: {
      type: Boolean,
      default: () => null,
    },
    isOnboarding: {
      type: Boolean,
      default: false,
    },
    previewLink: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
    automaticIntegration: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    links: {
      type: Object,
      default: () => ({
        website: '',
      }),
    },
    address: {
      type: Object,
      default: () => ({
        street: '',
        zip: '',
        city: '',
      }),
    },
    mailProvider: {
      type: String,
      default: null,
    },
    hideSecondaryActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showIntegrationWarning: false,
      data: {
        companyId: this.companyId,
        slogan: this.slogan,
        ambassador: this.ambassador,
        theme: this.theme,
        images: this.images,
        isReferenceCustomer: this.isReferenceCustomer,
        isOnboarding: this.isOnboarding,
        previewLink: this.previewLink,
        name: this.name,
        links: this.links,
        address: this.address,
        mailProvider: this.mailProvider,
        automaticIntegration: this.automaticIntegration,
      },
      localData: {},
    }
  },
  computed: {
    refetchPendingChanges() {
      return {
        query: PENDING_CHANGES_FOR_REGENERATION,
        variables: { companyId: this.companyId },
      }
    },
    refetchCompany() {
      return {
        query: COMPANY,
        variables: { id: this.companyId },
      }
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newVal) {
        this.localData = { ...newVal }
      },
      deep: true,
    },
  },
  methods: {
    updateData(newData) {
      this.localData = { ...this.localData, ...newData }
      this.data = { ...this.data, ...newData }
    },
    requiredData({
      ambassador: {
        firstname,
        lastname,
        role,
        phone,
        email,
        mobile,
        image: { url: imageUrl },
      },
      theme: {
        colors: { primary, secondary },
      },
      images: {
        logo: { url: logoUrl },
        team: { url: teamUrl },
      },
    }) {
      return {
        firstname,
        lastname,
        role,
        ...(!this.isPlatformTurkoglu && {
          phone,
        }),
        email,
        imageUrl,
        primary,
        secondary,
        mobile,
        teamUrl,
        ...(this.isLogoRequired() && {
          logoUrl,
        }),
      }
    },
    isLogoRequired() {
      const companyFeature = this.getFeature('company')
      return companyFeature.config.isLogoEditable
    },
    hasChanges(oldData, newData) {
      const getComparisonData = (data) => ({
        images: data.images,
        slogan: data.slogan,
        ambassador: data.ambassador,
        theme: data.theme,
        isReferenceCustomer: data.isReferenceCustomer,
        isOnboarding: data.isOnboarding,
        automaticIntegration: data.automaticIntegration,
      })

      return (
        JSON.stringify(getComparisonData(oldData)) !== JSON.stringify(getComparisonData(newData))
      )
    },
    async submit(data) {
      await Promise.all([
        this.updateAmbassador(data.ambassador),
        this.updateImages(),
        this.updateColors(data.theme.colors),
        this.updateSlogan(data.slogan),
        this.updateAutomaticIntegration(data.automaticIntegration),
        ...(this.isOnboarding ? [this.updateIsReferenceCustomer(data.isReferenceCustomer)] : []),
      ])
      if (this.data.automaticIntegration)
        this.$tracking.event('Onboarding', 'Selected', 'Makler Mittel Partner')
    },
    async updateAutomaticIntegration(automaticIntegration) {
      await this.$apollo.mutate({
        mutation: UPDATE_COMPANY_AUTOMATIC_INTEGRATION,
        variables: {
          input: {
            companyId: this.companyId,
            automaticIntegration,
          },
        },
      })
      this.showIntegrationWarning = !automaticIntegration
    },
    async updateColors({ primary, secondary }) {
      await this.$apollo.mutate({
        mutation: UPDATE_COMPANY_THEME,
        variables: {
          input: {
            companyId: this.companyId,
            colors: {
              primary,
              secondary,
            },
          },
        },
        refetchQueries: [this.refetchPendingChanges],
      })
    },
    async updateSlogan(slogan) {
      await this.$apollo.mutate({
        mutation: UPDATE_SLOGAN,
        variables: {
          input: {
            companyId: this.companyId,
            slogan,
          },
        },
        refetchQueries: [this.refetchPendingChanges],
      })
    },
    async updateIsReferenceCustomer(isReferenceCustomer) {
      await this.$apollo.mutate({
        mutation: UPDATE_IS_REFERENCE_CUSTOMER,
        variables: {
          input: {
            companyId: this.companyId,
            isReferenceCustomer: Boolean(isReferenceCustomer),
          },
        },
        refetchQueries: [this.refetchPendingChanges],
      })
    },
    saveAmbassadorImage(image, type) {
      this.localData.ambassador[type] = {
        ...this.localData.ambassador[type],
        file: image,
        url: URL.createObjectURL(image),
      }
      this.data.ambassador[type] = {
        ...this.data.ambassador[type],
        file: image,
        url: URL.createObjectURL(image),
      }
    },
    saveImage(image, type) {
      this.data.images[type] = {
        ...this.data.images[type],
        file: image,
        url: URL.createObjectURL(image),
      }
      this.localData.images[type] = {
        ...this.localData.images[type],
        file: image,
        url: URL.createObjectURL(image),
      }
    },
    async updateImages() {
      const images = Object.keys(this.localData.images)
        .filter((key) => this.localData.images[key].file)
        .map((key) => ({
          type: key,
          image: {
            id: this.localData.images[key].id,
            file: this.localData.images[key].file,
          },
        }))

      await this.$apollo.mutate({
        mutation: UPDATE_ONBOARDING_COMPANY_IMAGES,
        variables: {
          input: {
            companyId: this.companyId,
            images,
          },
        },
        refetchQueries: [this.refetchCompany, this.refetchPendingChanges],
      })
      bus.$emit(eventNames.TRIGGER_ANIMATION)
    },
    async deleteImage(type) {
      if (this.localData.images[type].id) {
        await this.$apollo.mutate({
          mutation: DELETE_COMPANY_IMAGE,
          variables: {
            input: {
              companyId: this.localData.companyId,
              type,
            },
          },
          update: () => {
            bus.$emit(eventNames.SHOW_SNACKBAR, {
              color: 'success',
              text: 'Erfolgreich gelöscht',
            })
          },
          refetchQueries: [this.refetchCompany, this.refetchPendingChanges],
        })
        this.$tracking.event('Settings', 'Clicked', 'Delete Image')
      }

      this.localData.images[type] = {}
      this.data.images[type] = {}
    },
    async updateAmbassador({ salutation, firstname, lastname, role, email, phone, mobile, image }) {
      await this.$apollo.mutate({
        mutation: UPDATE_COMPANY_AMBASSADOR,
        variables: {
          input: {
            companyId: this.companyId,
            salutation,
            firstname,
            lastname,
            role,
            email,
            phone: this.isPlatformTurkoglu && phone === null ? '' : phone,
            mobile,
            ...(image.file && {
              image: {
                id: image.id,
                file: image.file,
              },
            }),
          },
        },
        refetchQueries: [this.refetchCompany, this.refetchPendingChanges],
      })
    },
  },
}
</script>
